import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Search, Shield, Network, Phone, LayoutGrid, Mail, Headphones, 
  Database, ChevronRight, Monitor, Lock, Wifi, Key, Globe, ArrowRight
} from 'lucide-react';

const HERO_BG = "https://images.pexels.com/photos/8357337/pexels-photo-8357337.jpeg";
const REMOTE_WORK_IMG = "https://images.pexels.com/photos/5244024/pexels-photo-5244024.jpeg";

const Navigation = () => {
  const navigate = useNavigate();

  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#00F0FF] flex items-center justify-center">
            <Shield className="w-6 h-6 text-black" />
          </div>
          <span className="font-bold text-xl tracking-tight">Digital Associates</span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <Link to="/search" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-search">
            AI Search
          </Link>
          <Link to="/services" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-services">
            Services
          </Link>
          <Link to="/vdi" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-vdi">
            Remote Access
          </Link>
          <Link to="/contact" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-contact">
            Contact
          </Link>
        </div>

        <div className="flex items-center gap-4">
          <Link to="/contact">
            <Button 
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow font-medium"
              data-testid="contact-btn"
            >
              Get in Touch
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section 
      className="hero-bg min-h-screen flex items-center relative"
      style={{ backgroundImage: `url(${HERO_BG})` }}
      data-testid="hero-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 bg-[#151515] border border-[#27272A] px-4 py-2 mb-6">
            <div className="w-2 h-2 bg-[#00FF94] rounded-full animate-pulse" />
            <span className="text-sm text-[#A1A1AA] font-mono">ENTERPRISE IT SOLUTIONS</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            Your IT Infrastructure,{' '}
            <span className="text-[#00F0FF]">Secured & Optimized</span>
          </h1>
          
          <p className="text-xl text-[#A1A1AA] mb-10 leading-relaxed max-w-2xl">
            AI-powered IT support, secure remote access, and comprehensive enterprise solutions. 
            Digital Associates keeps your business running at peak performance.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => navigate('/search')}
              size="lg"
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow px-8 py-6 text-lg"
              data-testid="hero-search-btn"
            >
              <Search className="w-5 h-5 mr-2" />
              Try AI Search
            </Button>
            <Button 
              onClick={() => navigate('/services')}
              size="lg"
              variant="outline"
              className="border-[#27272A] hover:bg-[#151515] px-8 py-6 text-lg"
              data-testid="hero-services-btn"
            >
              View Services
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const FeaturesGrid = () => {
  const features = [
    { icon: Search, title: 'AI IT Search', desc: 'Get instant answers to enterprise IT questions', color: '#00F0FF' },
    { icon: Monitor, title: 'VDI Access', desc: 'Secure virtual desktop infrastructure', color: '#00FF94' },
    { icon: Lock, title: 'Cybersecurity', desc: 'Comprehensive threat protection', color: '#FF4D00' },
    { icon: Wifi, title: 'Network Management', desc: 'LAN, WiFi & infrastructure optimization', color: '#00F0FF' },
    { icon: Key, title: 'Password Management', desc: 'Secure credential solutions', color: '#00FF94' },
    { icon: Globe, title: 'Remote Support', desc: '24/7 technical assistance', color: '#FF4D00' },
  ];

  return (
    <section className="py-24 px-6 grid-bg" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Enterprise IT, <span className="text-[#00F0FF]">Simplified</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Everything you need to manage, secure, and optimize your business infrastructure
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="service-card p-6 group cursor-pointer"
              data-testid={`feature-card-${index}`}
            >
              <div 
                className="w-12 h-12 flex items-center justify-center mb-4"
                style={{ backgroundColor: `${feature.color}20` }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-[#00F0FF] transition-colors">
                {feature.title}
              </h3>
              <p className="text-[#A1A1AA]">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SearchPreview = () => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate('/search', { state: { initialQuery: query } });
    }
  };

  return (
    <section className="py-24 px-6 bg-[#0A0A0A]" data-testid="search-preview-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            AI-Powered <span className="text-[#00F0FF]">IT Search</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg">
            Get instant answers to your enterprise IT questions
          </p>
        </motion.div>

        <motion.form 
          onSubmit={handleSearch}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="terminal-card"
        >
          <div className="terminal-header">
            <div className="terminal-dot red" />
            <div className="terminal-dot yellow" />
            <div className="terminal-dot green" />
            <span className="text-sm text-[#A1A1AA] ml-2 font-mono">it-search</span>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3">
              <span className="text-[#00FF94] font-mono">$</span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="How do I configure VPN for remote workers?"
                className="search-input flex-1 p-4 text-white placeholder-[#A1A1AA]"
                data-testid="search-preview-input"
              />
              <Button 
                type="submit"
                className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow px-6"
                data-testid="search-preview-btn"
              >
                <Search className="w-5 h-5" />
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {['WiFi troubleshooting', 'Password policies', 'Firewall setup', 'VPN configuration'].map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setQuery(suggestion)}
                  className="text-sm px-3 py-1 bg-[#151515] border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#00F0FF] transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

const VDISection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 px-6" data-testid="vdi-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Secure <span className="text-[#00FF94]">Remote Access</span>
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8 leading-relaxed">
              Connect to your organization's infrastructure securely from anywhere. 
              Our VPN and VDI solutions provide enterprise-grade security with seamless access.
            </p>
            <ul className="space-y-4 mb-8">
              {[
                'Multi-factor authentication',
                'End-to-end encryption',
                '24/7 availability monitoring',
                'Instant virtual desktop deployment'
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#00FF94]/20 flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-[#00FF94]" />
                  </div>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button 
              onClick={() => navigate('/vdi')}
              className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 px-8"
              data-testid="vdi-access-btn"
            >
              Access Remote Portal
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src={REMOTE_WORK_IMG} 
              alt="Remote worker accessing secure systems"
              className="w-full h-auto rounded-md border border-[#27272A]"
            />
            <div className="absolute -bottom-6 -left-6 bg-[#0A0A0A] border border-[#27272A] p-4">
              <div className="flex items-center gap-3">
                <div className="status-dot online" />
                <span className="font-mono text-sm">VDI-001: Active</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesPreview = () => {
  const navigate = useNavigate();
  const services = [
    { icon: LayoutGrid, name: 'Microsoft 365', desc: 'Complete M365 deployment & management' },
    { icon: Mail, name: 'Google Workspace', desc: 'Gmail, Drive & collaborative tools' },
    { icon: Phone, name: 'VoIP Telephony', desc: 'Enterprise voice solutions' },
    { icon: Network, name: 'Network Infrastructure', desc: 'LAN, WAN & WiFi optimization' },
  ];

  return (
    <section className="py-24 px-6 bg-[#0A0A0A]" data-testid="services-preview-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
        >
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h2>
            <p className="text-[#A1A1AA] text-lg max-w-xl">
              Comprehensive IT solutions tailored for modern enterprises
            </p>
          </div>
          <Button 
            onClick={() => navigate('/services')}
            variant="outline"
            className="border-[#27272A] hover:bg-[#151515] mt-6 md:mt-0"
            data-testid="view-all-services-btn"
          >
            View All Services
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="service-card p-6 cursor-pointer"
              onClick={() => navigate('/services')}
              data-testid={`service-preview-${index}`}
            >
              <service.icon className="w-10 h-10 text-[#00F0FF] mb-4" />
              <h3 className="text-lg font-bold mb-2">{service.name}</h3>
              <p className="text-sm text-[#A1A1AA]">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 px-6" data-testid="cta-section">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="vdi-card p-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to <span className="text-[#00F0FF]">Transform</span> Your IT?
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses that trust Digital Associates for their IT infrastructure, 
            security, and support needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => navigate('/contact')}
              size="lg"
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow px-8"
              data-testid="cta-contact-btn"
            >
              Get in Touch
            </Button>
            <Button 
              onClick={() => navigate('/services')}
              size="lg"
              variant="outline"
              className="border-[#27272A] hover:bg-[#151515] px-8"
            >
              View Services
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="py-12 px-6 border-t border-[#27272A]" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#00F0FF] flex items-center justify-center">
                <Shield className="w-6 h-6 text-black" />
              </div>
              <span className="font-bold text-xl">Digital Associates</span>
            </div>
            <p className="text-[#A1A1AA] text-sm">
              Enterprise IT solutions for modern businesses.
            </p>
          </div>
          
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-[#A1A1AA]">Services</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="text-[#A1A1AA] hover:text-white transition-colors">All Services</Link></li>
              <li><Link to="/search" className="text-[#A1A1AA] hover:text-white transition-colors">AI IT Search</Link></li>
              <li><Link to="/vdi" className="text-[#A1A1AA] hover:text-white transition-colors">Remote Access</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-[#A1A1AA]">Solutions</h4>
            <ul className="space-y-2 text-sm text-[#A1A1AA]">
              <li>Microsoft 365</li>
              <li>Google Workspace</li>
              <li>Network Security</li>
              <li>VoIP Telephony</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-[#A1A1AA]">Contact</h4>
            <ul className="space-y-2 text-sm text-[#A1A1AA]">
              <li>support@digital-associates.co.uk</li>
              <li>0203 582 0582</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-[#27272A] flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-[#A1A1AA]">
            © 2025 Digital Associates. All rights reserved.
          </p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="#" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Privacy</a>
            <a href="#" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="landing-page">
      <Navigation />
      <HeroSection />
      <FeaturesGrid />
      <SearchPreview />
      <VDISection />
      <ServicesPreview />
      <CTASection />
      <Footer />
    </div>
  );
}
